import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BookDemoModal from "../components/BookDemoModal";
import { useTheme } from "../context/ThemeContext";
import "./LandingPage.css";
import { Link } from "react-router-dom";

export default function LandingPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false); // ADD THIS
  const [isScrolled, setIsScrolled] = useState(false); // Add scroll state

  // Add scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <div className="container">
        <div className="background-overlay"></div>

      {/* Same navbar as landing page with floating effect */}
      <div className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="logo-container">
          <Link to="/">
            <img src="/ob_logo_small_white.png" alt="OffsetBridge logo" className="logo-img" />
          </Link>
          <Link to="/" className="logo-text">OffsetBridge</Link>
        </div>
          {/* Desktop Navigation */}
          <div className="nav-buttons desktop-nav">
            <button
              className="theme-toggle-btn"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <button className="howItWorks-btn" onClick={() => navigate("/how-it-works")}>
              How It Works
            </button>
            <button className="signup-btn" onClick={() => navigate("/signup")}>
              Sign Up
            </button>
            <button className="login-btn" onClick={() => navigate("/login")}>
              Log In
            </button>
          </div>

          {/* Mobile Menu Button - BACK INSIDE NAVBAR */}
          <button 
            className="mobile-menu-btn" 
            onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          <button
            className="mobile-menu-item"
            onClick={() => {
              toggleTheme();
              setMobileMenuOpen(false);
            }}
          >
            {theme === 'dark' ? '☀️ Light Mode' : '🌙 Dark Mode'}
          </button>
          <button
            className="mobile-menu-item"
            onClick={() => {
              navigate("/how-it-works");
              setMobileMenuOpen(false);
            }}
          >
            How It Works
          </button>
          <button
            className="mobile-menu-item"
            onClick={() => {
              navigate("/signup");
              setMobileMenuOpen(false);
            }}
          >
            Sign Up
          </button>
          <button
            className="mobile-menu-item"
            onClick={() => {
              navigate("/login");
              setMobileMenuOpen(false);
            }}
          >
            Log In
          </button>
        </div>

        {/* Hero Section - Add padding-top to account for fixed navbar */}
        <div className="hero" style={{ paddingTop: '140px' }}>
          <img src="/logo.png" alt="OffsetBridge" className="hero-logo" />
          <h1 className="hero-title">OffsetBridge</h1>
          <p className="hero-subtitle">
            Discover who buys which carbon offsets.
          </p>
          <p className="hero-description">
            Stop wasting time on messy carbon offset data.
            Today, data is messy, fragmented, and locked behind complex or overpriced tools. OffsetBridge fixes that. 
            Clean, actionable retirement data in seconds, not hours.
          </p>
          <button className="hero-cta" onClick={() => setModalOpen(true)}>
            Book demo
          </button>
        </div>


          <div className="static-image-section">
            <img 
              src="/jpm_example_messy_names.svg" 
              alt="Beneficiary matching process" 
              className="process-diagram"
            />
          </div>

        {/* Analysis Intro Text */}
        <div className="analysis-intro">
          OffsetBridge analyzes data for you so that you can get to results
          quicker.
        </div>

        {/* Analysis Section */}
        <div className="section">
          <div className="analysis-container">
            <div className="analysis-box">
              <h3 className="analysis-title">Beneficiary analysis</h3>
              <div className="chart-container">
                <img
                  src="/line_chart.png"
                  alt="Line Chart"
                  className="chart-img"
                />
                <img
                  src="/pie_chart.png"
                  alt="Pie Chart"
                  className="chart-img"
                />
              </div>
            </div>
            <div className="analysis-box">
              <h3 className="analysis-title">Project analysis</h3>
              <img
                src="/projects_table.png"
                alt="Projects table"
                className="table-img"
              />
            </div>
          </div>
        </div>

        {/* Feature Section */}
        <div className="features">
          <div className="features-box">
            <h2 className="features-title">
              Figure out <span className="highlight">who buys what</span>.
            </h2>
            <p className="features-subtitle">
              VCM retirement data and analysis at your fingertips.
            </p>
            <ul className="features-list">
              <li className="feature-item">
                <span className="feature-heading">Comprehensive</span>
                <span className="feature-text">
                  OffsetBridge aggregates retirement data from the main VCM registries and identifies buyers. 
                  We update the data weekly and support
                  Verra, Gold Standard, ACR, Isometric, Puro Earth. More to come!
                </span>
              </li>
              <li className="feature-item">
                <span className="feature-heading">Clean</span>
                <span className="feature-text">
                  Registries differ in data structure, terminology, and beneficiary disclosure.
                  We clean, harmonize, and standardize this data into a consistent structure that’s ready for analysis.
                </span>
              </li>
              <li className="feature-item">
                <span className="feature-heading">Actionable</span>
                <span className="feature-text">
                Click on any beneficiary, intermediary, or project to view up-to-date analytics describing how 
                they behave on the market. Retirement histories, portfolio composition, top projects bought, and more. 
                Filter dynamically and find answers in seconds.
                </span>
              </li>
            </ul>
            <h2 className="features-title">
              <span className="highlight">Accelerate</span> your lead
              generation, market research, and go-to-market.
            </h2>
            <button className="feature-btn" onClick={() => setModalOpen(true)}>
              Book demo
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="footer">
          © 2025 OffsetBridge
          <Link to="/privacy-policy" className="privacy-link">
            Privacy Policy
          </Link>
          <Link to="/terms-and-conditions" className="terms-link">
            Terms & Conditions
          </Link>
        </div>
      </div>

      {/* Book Demo Modal at top level for proper stacking */}
      <BookDemoModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
